import React, { useMemo, useState } from "react";
import toast from "react-hot-toast";
import AdminLayout from "../admin/AdminDashboardLayout";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { ApiRequestOptions } from "../../utils/api";
import Button from "../../utils/Button";
import {
  CreditCard,
  Receipt,
  Clock3,
  ChevronLeft,
  ChevronRight,
  Download,
  ShieldCheck,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

interface PartnerInfo {
  id: number;
  name: string;
  orgType: string;
  partnerIdentifier: string;
  email: string;
  legalName: string;
  contact: string;
  website: string;
  enabled: boolean;
  status: string;
}

interface PaymentRecord {
  id: number;
  stripePaymentIntentId: string | null;
  stripeInvoiceId: string | null;
  stripeSubscriptionId: string | null;
  stripeSessionId: string | null;
  partner: PartnerInfo;
  status: "PENDING" | "SUCCEEDED" | "FAILED" | string;
  type: string;
  amount: number | null;
  currency: string;
  description: string | null;
  reference: string;
  createdAt: string;
  updatedAt: string;
}

interface PaymentsResponse {
  success: boolean;
  message: string;
  data: {
    content: PaymentRecord[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
}

interface BillingPortalResponse {
  success: boolean;
  message: string;
  data: {
    url?: string;
    portalUrl?: string;
    checkoutUrl?: string;
  };
}

const PAYMENT_METHOD_RETURN_URL = "https://123-vote.com/partner/payment/change";

const badgeStyles: Record<string, string> = {
  PENDING: "bg-amber-50 text-amber-700 border-amber-200",
  SUCCEEDED: "bg-emerald-50 text-emerald-700 border-emerald-200",
  FAILED: "bg-rose-50 text-rose-700 border-rose-200",
  DEFAULT: "bg-slate-50 text-slate-700 border-slate-200",
};

const currencyFormat = (amount: number | null, currency: string) => {
  if (amount === null || Number.isNaN(amount)) return "N/A";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "USD").toUpperCase(),
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const sanitizeFilenamePart = (value: string) =>
  value.replace(/[^a-z0-9-_]+/gi, "-").replace(/^-+|-+$/g, "") || "payment";

const StatusBadge = ({ status }: { status: string }) => {
  const key = status?.toUpperCase();
  const style = badgeStyles[key] || badgeStyles.DEFAULT;
  return (
    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${style}`}>
      <ShieldCheck className="w-3 h-3" />
      {key || "UNKNOWN"}
    </span>
  );
};

export default function Payments() {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const pageSize = 10;

  const { mutate: openBillingPortal, isPending: isOpeningBillingPortal } =
    useApiMutation<BillingPortalResponse>({
      onSuccess: (response) => {
        const portalUrl =
          response?.data?.portalUrl ||
          response?.data?.url ||
          response?.data?.checkoutUrl;

        if (portalUrl) {
          toast.success("Redirecting to Stripe to update your payment method...");
          window.location.href = portalUrl;
          return;
        }

        toast.error("Billing portal link unavailable. Please try again.");
      },
      onError: (error: unknown) => {
        const message =
          (error as { response?: { data?: { message?: string } } })?.response?.data
            ?.message || "Failed to open billing portal. Please try again.";
        toast.error(message);
      },
    });

  const handleChangePaymentMethod = () => {
    const returnUrl = encodeURIComponent(PAYMENT_METHOD_RETURN_URL);
    openBillingPortal({
      method: "GET",
      url: `/partner/billing/portal?returnUrl=${returnUrl}`,
    });
  };

  const { data, isLoading, error, refetch, isFetching } = useApiQuery<PaymentsResponse>({
    queryKey: ["partner", "payments", page, pageSize],
    request: {
      method: "GET",
      url: `/partner/payments?page=${page}&size=${pageSize}`,
    } as ApiRequestOptions,
    retry: 1,
  });

  const payments = data?.data?.content || [];
  const totalPages = data?.data?.totalPages || 0;
  const totalElements = data?.data?.totalElements || 0;

  const pageLabel = useMemo(() => {
    if (!data?.data) return "";
    const start = page * pageSize + 1;
    const end = Math.min(start + payments.length - 1, totalElements);
    return `${start}-${end} of ${totalElements}`;
  }, [data?.data, page, pageSize, payments.length, totalElements]);

  const handleDownloadReceipt = (payment: PaymentRecord) => {
    try {
      const pdf = new jsPDF();
      const partnerName =
        payment.partner?.legalName || payment.partner?.name || "Partner";
      const amount = currencyFormat(payment.amount, payment.currency);
      const createdAt = format(new Date(payment.createdAt), "MMM dd, yyyy hh:mm a");
      const updatedAt = format(new Date(payment.updatedAt), "MMM dd, yyyy hh:mm a");

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(20);
      pdf.text("123 Vote Payment Receipt", 20, 24);

      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(10);
      pdf.text("This receipt was generated from your partner payment history.", 20, 32);

      pdf.setDrawColor(226, 232, 240);
      pdf.line(20, 40, 190, 40);

      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.text("Payment Details", 20, 52);

      const fields: Array<[string, string]> = [
        ["Reference", payment.reference || `Payment #${payment.id}`],
        ["Partner", partnerName],
        ["Type", payment.type || "N/A"],
        ["Status", payment.status?.toUpperCase() || "UNKNOWN"],
        ["Amount", amount],
        ["Currency", (payment.currency || "USD").toUpperCase()],
        ["Description", payment.description || "No description"],
        ["Created", createdAt],
        ["Updated", updatedAt],
        ["Stripe Session ID", payment.stripeSessionId || "N/A"],
        ["Stripe Payment Intent ID", payment.stripePaymentIntentId || "N/A"],
        ["Stripe Invoice ID", payment.stripeInvoiceId || "N/A"],
      ];

      let y = 64;
      fields.forEach(([label, value]) => {
        pdf.setFont("helvetica", "bold");
        pdf.text(label, 20, y);
        pdf.setFont("helvetica", "normal");
        const wrappedValue = pdf.splitTextToSize(value, 115);
        pdf.text(wrappedValue, 75, y);
        y += Math.max(8, wrappedValue.length * 6);
      });

      pdf.setDrawColor(226, 232, 240);
      pdf.line(20, 265, 190, 265);
      pdf.setFontSize(9);
      pdf.text("Computer-generated receipt. No signature is required.", 20, 273);

      const filename = `123-vote-receipt-${sanitizeFilenamePart(
        payment.reference || String(payment.id)
      )}.pdf`;
      pdf.save(filename);
      toast.success("Receipt downloaded");
    } catch (downloadError) {
      console.error("Failed to generate receipt", downloadError);
      toast.error("Failed to download receipt. Please try again.");
    }
  };

  return (
    <AdminLayout role="PARTNER">
      <div className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-50 via-white to-slate-100">
        <div className="absolute -left-20 -top-10 h-64 w-64 rounded-full bg-emerald-200 blur-3xl opacity-40" />
        <div className="absolute right-0 top-20 h-72 w-72 rounded-full bg-sky-200 blur-3xl opacity-30" />

        <div className="relative mx-auto max-w-6xl px-4 py-10">
          <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-slate-500">Billing</p>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
                <CreditCard className="w-7 h-7 text-emerald-600" />
                Payment History
              </h1>
              <p className="text-sm text-slate-600 mt-2">
                Track every payment and billing event tied to your organization.
              </p>
            </div>
            <div className="flex gap-3">
              <Button
                variant="primary"
                size="medium"
                label="Change Plan"
                iconPosition="right"
                icon={<ArrowRight className="w-4 h-4" />}
                onClick={() => navigate('/partner/upgrade')}
              />
              <Button
                variant="secondary"
                size="large"
                label={isOpeningBillingPortal ? "Redirecting..." : "Change Payment Method"}
                icon={<CreditCard className="w-4 h-4" />}
                iconPosition="right"
                onClick={handleChangePaymentMethod}
                disabled={isOpeningBillingPortal}
              />
              <Button
                variant="tertiary"
                size="medium"
                label={isFetching ? "Refreshing..." : "Refresh"}
                icon={<Clock3 className="w-4 h-4" />}
                onClick={() => refetch()}
                disabled={isFetching}
              />
            </div>
          </div>

          <div className="overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <div className="flex items-center gap-2 text-sm text-slate-600">
                <Receipt className="w-4 h-4 text-emerald-600" />
                {isLoading ? "Loading payments..." : `${totalElements} total records`}
              </div>
              <div className="text-xs text-slate-500">{pageLabel}</div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wide text-xs">
                  <tr>
                    <th className="px-5 py-3">Reference</th>
                    <th className="px-5 py-3">Type</th>
                    <th className="px-5 py-3">Amount</th>
                    <th className="px-5 py-3">Status</th>
                    <th className="px-5 py-3">Created</th>
                    <th className="px-5 py-3">Updated</th>
                    <th className="px-5 py-3">Details</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {isLoading && (
                    <tr>
                      <td colSpan={7} className="px-5 py-6 text-center text-slate-500">
                        Loading payment history...
                      </td>
                    </tr>
                  )}

                  {!isLoading && payments.length === 0 && (
                    <tr>
                      <td colSpan={7} className="px-5 py-8 text-center text-slate-500">
                        No payments found yet.
                      </td>
                    </tr>
                  )}

                  {!isLoading &&
                    payments.map((p) => (
                      <tr key={p.id} className="hover:bg-slate-50/60 transition-colors">
                        <td className="px-5 py-4">
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-900">{p.reference}</span>
                            <span className="text-xs text-slate-500 truncate">
                              {p.description || "No description"}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-slate-800 font-medium">{p.type}</span>
                        </td>
                        <td className="px-5 py-4">
                          <span className="text-slate-900 font-semibold">
                            {currencyFormat(p.amount, p.currency)}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <StatusBadge status={p.status} />
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-slate-800">
                            {format(new Date(p.createdAt), "MMM dd, yyyy")}
                          </div>
                          <div className="text-xs text-slate-500">
                            {format(new Date(p.createdAt), "hh:mm a")}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="text-slate-800">
                            {format(new Date(p.updatedAt), "MMM dd, yyyy")}
                          </div>
                          <div className="text-xs text-slate-500">
                            {format(new Date(p.updatedAt), "hh:mm a")}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <button
                            type="button"
                            onClick={() => handleDownloadReceipt(p)}
                            className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 transition-colors hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2"
                            aria-label={`Download receipt for payment ${p.reference}`}
                            title="Download receipt"
                          >
                            <Download className="h-4 w-4 text-emerald-700" />
                          </button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between px-5 py-4 border-t border-slate-100 bg-slate-50/60">
              <div className="text-xs text-slate-500">{pageLabel}</div>
              <div className="flex items-center gap-3">
                <Button
                  variant="tertiary"
                  size="small"
                  label="Prev"
                  icon={<ChevronLeft className="w-4 h-4" />}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0 || isLoading}
                  additionalClassname="!w-auto px-4"
                />
                <span className="text-sm text-slate-700">
                  Page {page + 1} of {Math.max(totalPages, 1)}
                </span>
                <Button
                  variant="tertiary"
                  size="small"
                  label="Next"
                  icon={<ChevronRight className="w-4 h-4" />}
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1 || totalPages === 0 || isLoading}
                  additionalClassname="!w-auto px-4"
                />
              </div>
            </div>
          </div>

          {error && (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              Failed to load payments. Please try again.
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
